import { useState, useCallback, useMemo } from 'react';
import type { BCPSectionConfig } from '../config/bcpFieldConfig';

interface FieldContext {
  fieldId?: string;
  fieldLabel?: string;
  currentValue?: string;
  allFormData?: Record<string, unknown>;
}

interface UseAIConsultContextOptions {
  sections: BCPSectionConfig[];
  values: Record<string, string>;
  bcpType?: 'simple' | 'basic';
  onFieldValueChange?: (fieldId: string, value: string) => void;
}

interface UseAIConsultContextReturn {
  selectedFieldId: string | null;
  fieldContext: FieldContext | undefined;
  currentSection: string | undefined;
  currentField: string | undefined;
  bcpType: 'simple' | 'basic';
  handleAIConsult: (fieldId: string) => void;
  handleAIContentApply: (content: string) => void;
}

/**
 * AI相談機能の共通ロジックを提供するカスタムフック
 */
export function useAIConsultContext({
  sections,
  values,
  bcpType = 'simple',
  onFieldValueChange,
}: UseAIConsultContextOptions): UseAIConsultContextReturn {
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);

  // フィールドID→セクション＋フィールド情報のマッピング
  const fieldInfoMap = useMemo(() => {
    const map = new Map<
      string,
      { sectionTitle: string; fieldLabel: string; fieldId: string }
    >();

    sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.type !== 'static') {
          map.set(field.id, {
            sectionTitle: section.displayTitle ?? section.title,
            fieldLabel: field.label ?? field.id,
            fieldId: field.id,
          });
        }
      });
    });

    return map;
  }, [sections]);

  // フィールドコンテキストの生成
  const fieldContext = useMemo<FieldContext | undefined>(() => {
    if (!selectedFieldId) {
      return undefined;
    }

    const info = fieldInfoMap.get(selectedFieldId);
    if (!info) {
      return undefined;
    }

    return {
      fieldId: info.fieldId,
      fieldLabel: info.fieldLabel,
      currentValue: values[selectedFieldId],
      allFormData: values,
    };
  }, [selectedFieldId, fieldInfoMap, values]);

  // currentSection / currentField の生成
  const currentSection = useMemo(() => {
    if (!selectedFieldId) {
      return undefined;
    }
    return fieldInfoMap.get(selectedFieldId)?.sectionTitle;
  }, [selectedFieldId, fieldInfoMap]);

  const currentField = useMemo(() => {
    if (!selectedFieldId) {
      return undefined;
    }
    return fieldInfoMap.get(selectedFieldId)?.fieldLabel;
  }, [selectedFieldId, fieldInfoMap]);

  // AI相談開始
  const handleAIConsult = useCallback((fieldId: string) => {
    setSelectedFieldId(fieldId);
  }, []);

  // AI回答適用
  const handleAIContentApply = useCallback(
    (content: string) => {
      if (!selectedFieldId) {
        console.warn('selectedFieldId is null, cannot apply AI content');
        return;
      }

      if (onFieldValueChange) {
        onFieldValueChange(selectedFieldId, content);
      } else {
        console.warn('onFieldValueChange is not provided');
      }
    },
    [selectedFieldId, onFieldValueChange]
  );

  return {
    selectedFieldId,
    fieldContext,
    currentSection,
    currentField,
    bcpType,
    handleAIConsult,
    handleAIContentApply,
  };
}
